import type { editor as EN } from '../en/editor';

export const editor: Record<keyof typeof EN, string> = {
  'editor.saveState.saved': 'Gespeichert',
  'editor.saveState.saving': 'Wird gespeichert…',
  'editor.saveState.unsaved': 'Nicht gespeicherte Änderungen',
  'editor.saveState.local': 'Lokal gespeichert',
  'editor.saveState.conflict': 'Konflikt',

  /* ── Toolbar / bubble menu (shared labels) ───────────────────────────────── */
  'editor.toolbar.undo': 'Rückgängig',
  'editor.toolbar.redo': 'Wiederholen',
  'editor.toolbar.heading1': 'Überschrift 1',
  'editor.toolbar.heading2': 'Überschrift 2',
  'editor.toolbar.heading3': 'Überschrift 3',
  'editor.toolbar.bold': 'Fett',
  'editor.toolbar.italic': 'Kursiv',
  'editor.toolbar.underline': 'Unterstrichen',
  'editor.toolbar.strikethrough': 'Durchgestrichen',
  'editor.toolbar.inlineCode': 'Inline-Code',
  'editor.toolbar.link': 'Link',
  'editor.toolbar.bulletList': 'Aufzählung',
  'editor.toolbar.numberedList': 'Nummerierte Liste',
  'editor.toolbar.taskList': 'Aufgabenliste',
  'editor.toolbar.quote': 'Zitat',
  'editor.toolbar.codeBlock': 'Codeblock',
  'editor.toolbar.divider': 'Trennlinie',
  'editor.toolbar.table': 'Tabelle',
  'editor.toolbar.image': 'Bild',

  /* ── Gather child pages ───────────────────────────────────────────────────── */
  'editor.gatherChildren.label': 'Unterseiten zusammenführen',
  'editor.gatherChildren.untitled': 'Ohne Titel',
  'editor.gatherChildren.noChildren': 'Diese Seite hat keine Unterseiten zum Zusammenführen.',
  'editor.gatherChildren.confirmTitle': 'Inhalt dieser Seite ersetzen?',
  'editor.gatherChildren.confirmBody':
    'Der Inhalt von {count} Unterseiten ersetzt den aktuellen Inhalt dieser Seite. Mit Strg/⌘+Z rückgängig machbar.',
  'editor.gatherChildren.confirm': 'Ersetzen',
  'editor.gatherChildren.done': '{count} Unterseiten zusammengeführt.',
  'editor.gatherChildren.error': 'Die Unterseiten konnten nicht zusammengeführt werden.',

  /* ── Highlight / text colour ──────────────────────────────────────────────── */
  'editor.color.highlight': 'Hervorheben',
  'editor.color.textColor': 'Textfarbe',
  'editor.color.remove': 'Farbe entfernen',
  'editor.color.amber': 'Bernstein',
  'editor.color.green': 'Grün',
  'editor.color.blue': 'Blau',
  'editor.color.purple': 'Lila',
  'editor.color.red': 'Rot',
  'editor.color.gray': 'Grau',

  'editor.wordCount.one': '{count} Wort',
  'editor.wordCount.other': '{count} Wörter',

  /* ── Placeholder text ─────────────────────────────────────────────────────── */
  'editor.placeholder.empty': "Tippe '/' für Befehle oder schreib einfach los…",
  'editor.placeholder.heading': 'Überschrift',

  /* ── Find & replace ───────────────────────────────────────────────────────── */
  'editor.search.label': 'Suchen und ersetzen',
  'editor.search.find': 'Suchen',
  'editor.search.findPlaceholder': 'Auf der Seite suchen',
  'editor.search.replace': 'Ersetzen',
  'editor.search.replacePlaceholder': 'Ersetzen durch',
  'editor.search.replaceOne': 'Ersetzen',
  'editor.search.replaceAll': 'Alle ersetzen',
  'editor.search.next': 'Nächster Treffer',
  'editor.search.previous': 'Vorheriger Treffer',
  'editor.search.caseSensitive': 'Groß-/Kleinschreibung beachten',
  'editor.search.showReplace': 'Ersetzen anzeigen',
  'editor.search.hideReplace': 'Ersetzen ausblenden',
  'editor.search.close': 'Schließen',
  'editor.search.noResults': 'Keine Treffer',
  'editor.search.matchCount': '{current}/{total}',

  /* ── Link prompt ──────────────────────────────────────────────────────────── */
  'editor.link.prompt': 'Link-URL',

  /* ── Wiki links ───────────────────────────────────────────────────────────── */
  'editor.wikiLink.brokenTitle':
    '{text} — diese Seite gibt es noch nicht. Klicke, um sie zu erstellen.',
  'editor.wikiLink.searching': 'Suche läuft…',
  'editor.wikiLink.noMatch': 'Keine Seiten gefunden für „{query}“.',
  'editor.wikiLink.createPagePrefix': 'Seite „',
  'editor.wikiLink.createPageSuffix': '“ erstellen',

  /* ── Recovery banner ──────────────────────────────────────────────────────── */
  'editor.recovery.unsavedChanges':
    'Du hast nicht gespeicherte lokale Änderungen von {time} ({ago}).',
  'editor.recovery.explanation':
    'Diese Seite wurde seitdem auch anderswo geändert. Nichts ist verloren gegangen — wähle, welche Version du behalten willst.',
  'editor.recovery.keepMine': 'Meine behalten',
  'editor.recovery.useServerVersion': 'Serverversion verwenden',
  'editor.recovery.showDiff': 'Unterschiede anzeigen',
  'editor.recovery.diffTitle': 'Deine Version vs. die des Servers',
  'editor.recovery.diffDescription':
    'Reiner Text, nur zum Vergleich. Es gibt keine automatische Zusammenführung — wähle eine Seite.',
  'editor.recovery.yourVersion': 'deine nicht gespeicherte Version',
  'editor.recovery.serverVersion': 'die des Servers',

  /* ── Uploads ──────────────────────────────────────────────────────────────── */
  'editor.upload.tooLarge': '{name} ist größer als 10 MB.',
  'editor.upload.unsupportedType': '{name} ist kein Bild, das wir speichern können.',
  'editor.upload.unreadable': '{name} konnte nicht als Bild gelesen werden.',
  'editor.upload.failed': 'Ein Bild konnte nicht hochgeladen werden.',
  'editor.upload.failedDescription':
    'Es ist auf diesem Gerät gespeichert. Nutze „Erneut versuchen“ am Bild, um es erneut zu versuchen.',

  'editor.image.uploading': 'Wird hochgeladen',
  'editor.image.notUploadedYet': 'Noch nicht hochgeladen — auf diesem Gerät gespeichert.',
  'editor.image.uploadFile': 'Datei hochladen…',
  'editor.image.fromUrl': 'Von URL…',
  'editor.image.urlPlaceholder': 'https://example.com/bild.png',
  'editor.image.add': 'Hinzufügen',
  'editor.image.urlInvalid': 'Gib eine gültige http(s)-Bild-URL ein.',

  /* ── Paste toasts ─────────────────────────────────────────────────────────── */
  'editor.paste.droppedLocalImages.one': '{count} Bild konnte nicht eingefügt werden.',
  'editor.paste.droppedLocalImages.other': '{count} Bilder konnten nicht eingefügt werden.',
  'editor.paste.droppedLocalImagesDescription':
    'Es war ein Link zu einer Datei auf deinem Computer, und die Zwischenablage enthielt nicht das Bild selbst. Kopiere das Bild direkt und füge es erneut ein.',
  'editor.paste.remoteImages.one':
    '{count} Bild verweist auf eine andere Website und könnte irgendwann nicht mehr funktionieren.',
  'editor.paste.remoteImages.other':
    '{count} Bilder verweisen auf eine andere Website und könnten irgendwann nicht mehr funktionieren.',
  'editor.paste.remoteImagesDescription':
    'Rechtsklick → Speichern, dann einfügen, um eine Kopie zu behalten.',
};
